'use client';

import { useId, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ChevronRight } from 'lucide-react';
import { Popover, CountBadge } from '@statura/ui';
import type { NavBadgeCounts, NavV2Group, NavV2Item } from '@/lib/navigation/nav-v2';
import { NavV2ContextMenu, type ContextMenuPosition } from './context-menu';
import { trackNavV2Event, type NavV2EventSource } from '@/lib/nav-v2/telemetry';

interface Props {
  /** Already filtered (role × program × RBAC × module) — sidebar parent does the gating. */
  groups: ReadonlyArray<NavV2Group & { items: readonly NavV2Item[] }>;
  /** Current pathname for active-state highlighting. */
  currentPath: string;
  /** Set of currently-pinned hrefs — drives the right-click menu's pin/unpin label. */
  pinnedSet: Set<string>;
  /** Toggle pin state (called from context menu). */
  onTogglePin: (href: string) => void;
  /** Real, already-scoped nav badge counts keyed by NavBadgeKey. */
  badgeCounts?: NavBadgeCounts;
  /**
   * Phase 6 telemetry source override. Defaults to `'sidebar-domain-tiles'`;
   * `NavV2Sidebar` overrides to `'mobile-drawer'` when rendered inside
   * `NavV2MobileDrawer`.
   */
  clickSource?: NavV2EventSource;
}

/**
 * Compact grid of domains. Each tile opens a popover listing every
 * visible item in that domain so the full surface is discoverable from
 * the sidebar (Paul UX feedback 2026-05-27 — pre-fix, clicking a tile
 * only navigated to the first item with no way to see the rest).
 *
 * - Left-click tile  → open popover of items
 * - Click popover item → navigate
 * - Right-click tile → context menu (Pin/Open/Copy link) on the domain
 *   entry item (same behaviour as before for muscle memory)
 * - Keyboard: Enter/Space opens; arrows move between items; Esc restores focus
 *
 * Active state: the tile of the domain that contains the current path
 * (prefix-match so nested detail routes still light the parent).
 */
export function DomainTiles({
  groups,
  currentPath,
  pinnedSet,
  onTogglePin,
  badgeCounts,
  clickSource = 'sidebar-domain-tiles',
}: Props) {
  const router = useRouter();
  const [menu, setMenu] = useState<ContextMenuPosition | null>(null);
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const descriptionPrefix = useId();
  const inMobileDrawer = clickSource === 'mobile-drawer';

  return (
    <div className="grid min-w-0 grid-cols-2 gap-2">
      {groups.map((group) => {
        const first = group.items[0];
        if (!first) return null;
        const isActive = group.items.some(
          (i) => currentPath === i.href || currentPath.startsWith(`${i.href}/`),
        );
        const Icon = group.icon;
        const isOpen = openGroupId === group.id;
        const groupBadgeCount = group.items.reduce(
          (sum, item) => sum + (item.badgeKey ? (badgeCounts?.[item.badgeKey] ?? 0) : 0),
          0,
        );
        const hasAttentionCount = group.items.some((item) => item.badgeKey != null);
        const hasUnknownAttention = group.items.some(
          (item) => item.badgeKey != null && badgeCounts?.[item.badgeKey] == null,
        );
        const attentionDescription = hasUnknownAttention
          ? groupBadgeCount > 0
            ? `${groupBadgeCount} known items need attention; some attention counts unavailable.`
            : 'Attention count unavailable.'
          : hasAttentionCount
            ? `${groupBadgeCount} items need attention.`
            : '';
        const descriptionId = `${descriptionPrefix}-${group.id}`;
        const linkDescription = `${group.items.length} navigation item${group.items.length === 1 ? '' : 's'}.`;

        const trigger = (
          <button
            type="button"
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu({
                x: e.clientX,
                y: e.clientY,
                target: { href: first.href, label: group.title, groupTitle: group.title },
              });
            }}
            className={[
              'relative flex min-h-24 min-w-0 flex-col items-start gap-1.5 rounded-tile border p-3 text-left text-body',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus forced-colors:focus-visible:outline forced-colors:focus-visible:outline-2 forced-colors:focus-visible:outline-offset-2 forced-colors:focus-visible:outline-focus',
              isActive
                ? 'border-selected-foreground bg-selected font-semibold text-selected-foreground forced-colors:outline forced-colors:outline-2'
                : 'border-control-border/30 bg-surface-sheet text-text-body hover:bg-surface-soft',
              isOpen ? 'ring-2 ring-focus' : '',
            ].join(' ')}
            aria-label={group.title}
            aria-describedby={descriptionId}
            aria-current={isActive ? 'page' : undefined}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            data-navigation-tile="domain"
            data-nav-v2-domain-tile={group.id}
            data-active={isActive ? 'true' : 'false'}
          >
            <span className="flex w-full min-w-0 items-start justify-between gap-2">
              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-surface-soft">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              {groupBadgeCount > 0 && (
                <CountBadge
                  compact
                  count={groupBadgeCount}
                  label={
                    hasUnknownAttention ? 'known items need attention' : 'items need attention'
                  }
                  className="bg-status-warning-bg text-status-warning-fg"
                />
              )}
            </span>
            <span className="min-w-0 w-full whitespace-normal">{group.title}</span>
            <span id={descriptionId} className="sr-only">
              {linkDescription} {attentionDescription}
            </span>
          </button>
        );

        return (
          <Popover
            key={group.id}
            trigger={trigger}
            side={inMobileDrawer ? 'right' : 'bottom'}
            align="start"
            open={isOpen}
            onOpenChange={(next) => setOpenGroupId(next ? group.id : null)}
            focusInteractiveOnOpen
            className="ds-shell-navigation rounded-overlay shadow-popover w-64 max-w-full max-h-96 p-1"
            role="menu"
            ariaLabel={group.title}
            // Render above the mobile nav drawer (z-70); on desktop this is inert.
            zIndex={85}
          >
            <div className="border-b border-control-border/30 px-2.5 py-1.5">
              <p className="text-caption font-semibold uppercase tracking-wider text-selected-foreground">
                {group.title}
              </p>
              {group.description && (
                <p className="mt-0.5 text-caption text-text-muted">{group.description}</p>
              )}
              <p className="mt-0.5 text-caption text-text-muted">
                {linkDescription} {attentionDescription}
              </p>
            </div>
            <ul
              role="none"
              className="flex flex-col gap-px py-1"
              onKeyDown={(event) => {
                const items = Array.from(
                  event.currentTarget.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]'),
                );
                const index = items.findIndex((item) => item === document.activeElement);
                let nextIndex: number;
                switch (event.key) {
                  case 'ArrowDown':
                    nextIndex = (index + 1) % items.length;
                    break;
                  case 'ArrowUp':
                    nextIndex = index <= 0 ? items.length - 1 : index - 1;
                    break;
                  case 'Home':
                    nextIndex = 0;
                    break;
                  case 'End':
                    nextIndex = items.length - 1;
                    break;
                  default:
                    return;
                }
                event.preventDefault();
                items[nextIndex]?.focus();
              }}
            >
              {group.items.map((item, index) => {
                const ItemIcon = item.icon;
                const itemActive =
                  currentPath === item.href || currentPath.startsWith(`${item.href}/`);
                const badgeCount = item.badgeKey ? badgeCounts?.[item.badgeKey] : undefined;
                const previousGroupTitle = group.items[index - 1]?.complianceGroupTitle;
                const startsComplianceGroup =
                  item.complianceGroupTitle != null &&
                  item.complianceGroupTitle !== previousGroupTitle;
                return (
                  <li key={item.href} role="none">
                    {startsComplianceGroup ? (
                      <p className="px-2 pb-0.5 pt-2 text-caption font-semibold uppercase tracking-wider text-text-muted">
                        {item.complianceGroupTitle}
                      </p>
                    ) : null}
                    <Link
                      role="menuitem"
                      href={item.href}
                      onClick={() => {
                        trackNavV2Event('nav_v2_item_clicked', {
                          source: clickSource,
                          route: item.href,
                          variant: 'tile-popover',
                        });
                        setOpenGroupId(null);
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setMenu({
                          x: e.clientX,
                          y: e.clientY,
                          target: { href: item.href, label: item.label, groupTitle: group.title },
                        });
                      }}
                      aria-current={itemActive ? 'page' : undefined}
                      className={[
                        'flex items-center gap-2 rounded-control px-2 py-1.5 text-caption',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                        itemActive
                          ? 'bg-selected font-semibold text-selected-foreground'
                          : 'text-text-heading hover:bg-surface-canvas',
                      ].join(' ')}
                    >
                      <ItemIcon className="h-3 w-3 shrink-0 text-text-muted" aria-hidden="true" />
                      <span className="flex-1 truncate">{item.label}</span>
                      {badgeCount != null && badgeCount > 0 && (
                        <CountBadge
                          compact
                          count={badgeCount}
                          label="items need attention"
                          className="self-center bg-status-warning-bg text-status-warning-fg"
                        />
                      )}
                      {item.badgeKey && badgeCount == null && (
                        <span className="sr-only">Attention count unavailable</span>
                      )}
                      <ChevronRight className="h-3 w-3 text-text-muted" aria-hidden="true" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </Popover>
        );
      })}

      <NavV2ContextMenu
        position={menu}
        isPinned={menu ? pinnedSet.has(menu.target.href) : false}
        onTogglePin={onTogglePin}
        onOpen={(href) => {
          trackNavV2Event('nav_v2_item_clicked', {
            source: clickSource,
            route: href,
            variant: 'context-menu',
          });
          router.push(href);
        }}
        onClose={() => setMenu(null)}
      />
    </div>
  );
}
